/**
 * Consent Storage Management
 * 
 * Handles localStorage and cookie storage for consent preferences.
 * Uses both localStorage (for SPA) and a cookie (for static pages and cross-subdomain).
 */

import { CONFIG_VERSION, CONSENT_EXPIRY_DAYS, ConsentState, DEFAULT_CONSENT_STATE } from './consentConfig';

/**
 * Generate a UUID v4 without external dependency
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const STORAGE_KEY = 'sb_consent';
const COOKIE_NAME = 'sb_consent';

export interface StoredConsent {
  consentId: string;
  configVersion: string;
  state: ConsentState;
  timestamp: number; // Unix timestamp in ms
  expiresAt: number; // Unix timestamp in ms
}

/**
 * Generate a unique consent ID for audit trail
 */
export function generateConsentId(): string {
  return generateUUID();
}

/**
 * Get consent from localStorage
 */
export function getStoredConsent(): StoredConsent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const parsed: StoredConsent = JSON.parse(stored);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Save consent to localStorage and cookie
 */
export function saveConsent(state: ConsentState, existingConsentId?: string): StoredConsent {
  const now = Date.now();
  const expiresAt = now + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
  
  const consent: StoredConsent = {
    consentId: existingConsentId || generateConsentId(),
    configVersion: CONFIG_VERSION,
    state: {
      necessary: true, // Always true
      analytics: state.analytics,
      marketing: state.marketing,
    },
    timestamp: now,
    expiresAt,
  };
  
  // Save to localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  
  // Save minimal info to cookie for static pages
  // Cookie format: version|analytics|marketing|consentId
  const cookieValue = `${consent.configVersion}|${consent.state.analytics ? '1' : '0'}|${consent.state.marketing ? '1' : '0'}|${consent.consentId}`;
  const expiryDate = new Date(expiresAt).toUTCString();
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(cookieValue)}; expires=${expiryDate}; path=/; SameSite=Lax`;
  
  return consent;
}

/**
 * Parse consent from cookie (for static pages)
 */
export function getConsentFromCookie(): StoredConsent | null {
  try {
    const cookies = document.cookie.split(';');
    const consentCookie = cookies.find(c => c.trim().startsWith(`${COOKIE_NAME}=`));
    
    if (!consentCookie) return null;
    
    const value = decodeURIComponent(consentCookie.split('=')[1]);
    const [version, analytics, marketing, consentId] = value.split('|');
    
    return {
      consentId: consentId || generateConsentId(),
      configVersion: version,
      state: {
        necessary: true,
        analytics: analytics === '1',
        marketing: marketing === '1',
      },
      timestamp: Date.now(), // Not stored in cookie, use current time
      expiresAt: Date.now() + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000),
    };
  } catch {
    return null;
  }
}

/**
 * Check if consent needs to be refreshed (expired or version mismatch)
 */
export function needsReconsent(stored: StoredConsent | null): boolean {
  if (!stored) return true;
  
  // Version mismatch - config changed
  if (stored.configVersion !== CONFIG_VERSION) return true;
  
  // Consent expired
  if (Date.now() > stored.expiresAt) return true;
  
  return false;
}

/**
 * Clear all consent data
 */
export function clearConsent(): void {
  localStorage.removeItem(STORAGE_KEY);
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Get current consent state, falling back to defaults
 */
export function getCurrentConsentState(): ConsentState {
  const stored = getStoredConsent() || getConsentFromCookie();
  
  if (stored && !needsReconsent(stored)) {
    return stored.state;
  }
  
  return DEFAULT_CONSENT_STATE;
}

/**
 * Check if user has made a consent decision
 */
export function hasConsentDecision(): boolean {
  const stored = getStoredConsent() || getConsentFromCookie();
  return stored !== null && !needsReconsent(stored);
}

